import { useParams } from 'react-router'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Kap_Journal, Kap_Program, Query_Root } from 'src/@types/graphql'
import { queryKapJournalsById } from 'src/screens/kap/program/journal/kapJournalQueries'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Section } from 'src/shared/presentation/Section'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { KapProgramJournalForm } from 'src/screens/kap/program/journal/edit/KapProgramJournalForm'
import {
  fetchKapProgramByIdQuery,
  queryModulesOfProgramsInCanton,
} from 'src/screens/kap/program/details/kapProgramQueries'
import { MODULE_TYPES } from 'src/shared/constants/constants'

type originType = 'SAVE' | 'SAVE_AND_BACK'

export const KapProgramJournalEditPage = () => {
  const { programId, journalId } = useParams()
  const programIdInt = parseInt(programId as string)
  const journalIdInt = parseInt(journalId as string)
  const { getMessage } = useMessageSource()

  const navigate = useDelayedNavigate()

  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const originRef = useRef<originType>('SAVE')

  const [kapJournal, setKapJournal] = useState<Kap_Journal>()
  const [kapProgram, setKapProgram] = useState<Kap_Program>()
  const [modulesInCanton, setModulesInCanton] = useState<Set<MODULE_TYPES>>(() => new Set())

  const onSubmit = (origin: originType) => {
    if (originRef) {
      originRef.current = origin
    }

    document.getElementById('kap-journal-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const onBack = useCallback(
    (kapJournalId?: number) => {
      const route = ROUTES.KapProgramJournal.params({ programId })

      if (kapJournalId) {
        navigate(`${route}#${kapJournalId}`, { state: { expandedJournalId: kapJournalId } })
      } else {
        navigate(route)
      }
    },
    [programId, navigate],
  )

  const fetchJournalToEditData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{
        kap_journal_by_pk: Query_Root['kap_journal_by_pk']
      }>(queryKapJournalsById, {
        id: journalIdInt,
      })
      .toPromise()

    if (!data?.kap_journal_by_pk) {
      throw new Error('Resource not found')
    }

    return data.kap_journal_by_pk
  }, [journalIdInt, urqlClient])

  const fetchKapProgramData = useCallback(async (): Promise<Kap_Program> => {
    const { data } = await urqlClient
      .query<{ kap_program_by_pk: Query_Root['kap_program_by_pk'] }>(fetchKapProgramByIdQuery, {
        id: programIdInt,
      })
      .toPromise()

    if (!data?.kap_program_by_pk) {
      throw new Error('Resource not found')
    }

    return data.kap_program_by_pk
  }, [programIdInt, urqlClient])

  const fetchKapProgramsInCantonWithModulesData = useCallback(
    async (fetchedKapProgram: Kap_Program) => {
      const { data } = await urqlClient
        .query<{ kap_program: Query_Root['kap_program'] }>(queryModulesOfProgramsInCanton, {
          canton_code: fetchedKapProgram.canton_code,
        })
        .toPromise()

      if (!data?.kap_program) {
        throw new Error('Resource not found')
      }

      return data.kap_program
    },
    [urqlClient],
  )

  const initData = useCallback(async () => {
    fetchJournalToEditData()
      .then((journal) => setKapJournal(journal))
      .catch(() => notificationService.operationFailed())

    try {
      const kapProgramData = await fetchKapProgramData()
      const programsInCanton = await fetchKapProgramsInCantonWithModulesData(kapProgramData)

      setKapProgram(kapProgramData)

      const uniqueFetchedModules = new Set<MODULE_TYPES>()
      programsInCanton.forEach((program) => {
        program.modules.forEach((module: MODULE_TYPES) => uniqueFetchedModules.add(module))
      })

      setModulesInCanton(uniqueFetchedModules)
    } catch (err) {
      notificationService.operationFailed()
    }
  }, [fetchJournalToEditData, fetchKapProgramData, fetchKapProgramsInCantonWithModulesData, notificationService])

  useEffect(() => {
    if (programIdInt) {
      initData()
    }
  }, [programIdInt, initData])

  const afterSubmit = () => {
    if (originRef.current === 'SAVE_AND_BACK') {
      onBack(parseInt(journalId as string))
    }
  }

  const { canEditJournals, loading } = usePermissionsForKapProgram(programIdInt)

  if (!loading && !canEditJournals) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.edit.program.journal')}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={() => onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={() => onSubmit('SAVE')} />
        </>
      }
      onBack={() => onBack(journalIdInt)}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        <Section helpAndInstructions={<HelpAndInstructions labelKey="label.help.program.journal" defaultExpansion />}>
          {kapJournal && kapProgram && modulesInCanton && (
            <KapProgramJournalForm
              afterSubmit={afterSubmit}
              kapProgram={kapProgram}
              kapJournal={kapJournal}
              modulesInCanton={modulesInCanton}
            />
          )}
        </Section>
      </PageLayout>
    </ScreenLayout>
  )
}
